import { useState, useCallback, useEffect } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getInternalTicketWorkflow } from '@/modules/Service/services/ticket'
import {
    InternalTicketWorkflow,
    InternalTicketWorkflowStatus,
    InternalTicketWorkflowStepName,
} from '@/modules/Service/types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

// Active statuses where we show full workflow
const ACTIVE_STATUSES = ['opening', 'work-in-progress', 'pending']

// CSKH step names for verification check
const CSKH_STEP_NAMES = [
    InternalTicketWorkflowStepName.CSKHConfirm,
    InternalTicketWorkflowStepName.CSKHVerifyCreateService,
    InternalTicketWorkflowStepName.CSKHConfirmCancel,
    InternalTicketWorkflowStepName.CSKHConfirmTaxPercentageUpdate,
    InternalTicketWorkflowStepName.CSKHXacNhan,
    InternalTicketWorkflowStepName.CSKHKiemTraHoanTat,
]

export const useInternalTicketWorkflow = (serviceId: number) => {
    const [expandedStep, setExpandedStep] = useState<number | null>(null)
    const [forceFullView, setForceFullView] = useState(false)
    const [selectedTicketIndex, setSelectedTicketIndex] = useState(0)
    const [isCopied, setIsCopied] = useState(false)

    const { data: tickets, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INTERNAL, 'tickets', serviceId],
        queryFn: () => getInternalTicketWorkflow(serviceId),
        enabled: !!serviceId,
    })

    // Auto-select first active ticket on load
    useEffect(() => {
        if (tickets && tickets.length > 0) {
            const activeTicketIndex = tickets.findIndex((ticket) =>
                ACTIVE_STATUSES.includes(ticket.ticket_status)
            )
            if (activeTicketIndex !== -1 && selectedTicketIndex === 0) {
                setSelectedTicketIndex(activeTicketIndex)
            }
        }
    }, [tickets, selectedTicketIndex])

    const activeTicket: InternalTicketWorkflow | undefined =
        tickets?.[selectedTicketIndex] || tickets?.[0]

    const isActiveWorkflow =
        (activeTicket &&
            ACTIVE_STATUSES.includes(activeTicket.ticket_status)) ||
        forceFullView

    // Auto-expand step when workflow is active
    useEffect(() => {
        if (tickets && tickets.length > 0 && activeTicket) {
            if (isActiveWorkflow && !expandedStep) {
                // First priority: find any "doing" step
                const doingStep = activeTicket.ticket_workflow_steps?.find(
                    (step) => step.status === InternalTicketWorkflowStatus.Doing
                )

                if (doingStep) {
                    setExpandedStep(doingStep.step_id)
                } else {
                    // Second priority: find first incomplete step (not done)
                    const incompleteStep = activeTicket.ticket_workflow_steps
                        ?.sort((a: any, b: any) => a.step_order - b.step_order)
                        ?.find(
                            (step) =>
                                step.status !==
                                InternalTicketWorkflowStatus.Done
                        )

                    if (incompleteStep) {
                        setExpandedStep(incompleteStep.step_id)
                    } else {
                        // Last resort: expand first step if all are complete
                        const firstStep =
                            activeTicket.ticket_workflow_steps?.sort(
                                (a: any, b: any) => a.step_order - b.step_order
                            )?.[0]
                        if (firstStep) {
                            setExpandedStep(firstStep.step_id)
                        }
                    }
                }
            }
        }
    }, [
        tickets,
        expandedStep,
        selectedTicketIndex,
        forceFullView,
        activeTicket,
        isActiveWorkflow,
    ])

    // Check if any CSKH verification step is done
    const cskhVerificationStep = activeTicket?.ticket_workflow_steps?.find(
        (step) =>
            CSKH_STEP_NAMES.some(
                (name) => step.step_name?.toLowerCase() === name.toLowerCase()
            )
    )
    const isCSKHVerificationDone =
        cskhVerificationStep?.status === InternalTicketWorkflowStatus.Done

    // Handlers
    const handleStepClick = useCallback(
        (stepId: number) => {
            setExpandedStep(expandedStep === stepId ? null : stepId)
        },
        [expandedStep]
    )

    const handleTicketSelect = useCallback((index: number) => {
        setSelectedTicketIndex(index)
        setExpandedStep(null) // Reset expanded step when switching tickets
    }, [])

    const handleCopyTicketLink = useCallback(() => {
        if (activeTicket?.ticket_id) {
            const ticketUrl = `${window.location.origin}/tickets/${activeTicket.ticket_id}`
            navigator.clipboard.writeText(ticketUrl)
            setIsCopied(true)
            setTimeout(() => setIsCopied(false), 2000)
        }
    }, [activeTicket?.ticket_id])

    const handleOpenTicket = useCallback(() => {
        if (activeTicket?.ticket_id) {
            window.open(`/tickets/${activeTicket.ticket_id}`, '_blank')
        }
    }, [activeTicket?.ticket_id])

    const handleToggleFullView = useCallback((value: boolean) => {
        setForceFullView(value)
    }, [])

    // Helper to check if step is clickable
    const isStepClickable = useCallback(
        (status: InternalTicketWorkflowStatus) => {
            return (
                status === InternalTicketWorkflowStatus.Done ||
                status === InternalTicketWorkflowStatus.Doing ||
                status === InternalTicketWorkflowStatus.Blocked
            )
        },
        []
    )

    // Check if ticket is active
    const isTicketActive = useCallback((ticketStatus: string) => {
        return ACTIVE_STATUSES.includes(ticketStatus)
    }, [])

    // Calculate progress percentage
    const progressPercentage = activeTicket?.ticket_workflow_steps
        ? (activeTicket.ticket_workflow_steps.filter(
              (s) => s.status === InternalTicketWorkflowStatus.Done
          ).length /
              activeTicket.ticket_workflow_steps.length) *
          100
        : 0

    return {
        // State
        expandedStep,
        forceFullView,
        selectedTicketIndex,
        isCopied,

        // Data
        tickets,
        activeTicket,
        renderState,

        // Computed
        isActiveWorkflow,
        isCSKHVerificationDone,
        progressPercentage,

        // Handlers
        handleStepClick,
        handleTicketSelect,
        handleCopyTicketLink,
        handleOpenTicket,
        handleToggleFullView,

        // Helpers
        isStepClickable,
        isTicketActive,
    }
}
